<template>
	<span class="d-none"> {{ layoutname }}</span>
	<component v-if="appReady" :is="currentLayout" :key="appKey">
		<router-view></router-view>
	</component>
</template>

<style lang="scss">
@import './assets/scss/velzon/config/default/app.scss';
@import './styles/vuestudio-organizer.scss';
</style>

<script setup>
import {
	ref,
	computed,
	watch,
	getCurrentInstance,
	onMounted,
	onBeforeMount,
	onBeforeUnmount,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { createServer } from '@/apis/https';
import { ensureValidAuthSession, loadAuth } from '@/store/auth';
import { loadConfig } from '@/store/config';
import { loadDb } from '@/store/db';
import { applyAccountBrowserPreferences } from '@/store/account-preferences';
import i18next from '@/i18n';
import { markFeatureAccessBootstrapReady } from '@/store/modules/feature-access';
import {
	loadThemeModePreference,
	resolveThemeMode,
} from '@/utils/theme-mode';

import AuthLayoutComponent from './components/layout/Auth/AuthLayoutComponent';
import GuestLayoutComponent from './components/layout/Guest/GuestLayoutComponent';

const components = {
	auth: AuthLayoutComponent,
	guest: GuestLayoutComponent,
};

const { proxy: vm } = getCurrentInstance();
vm.$router = useRouter();
vm.$route = useRoute();
vm.$store = useStore();
vm.$server = vm.$server || '';
vm.$themeColor = vm.$store.state.settings.config.layout.themeColor;

const appname = vm.$appname;
const store = vm.$store;
const route = vm.$route;

const normalizeLang = value => {
	if (!value) {
		return 'en';
	}
	const normalized = value.toLowerCase().replace('_', '-');
	const map = {
		en: 'en',
		'zh-tw': 'zh-tw',
		'zh-hant': 'zh-tw',
		'zh-hk': 'zh-tw',
		'zh-cn': 'zh-cn',
		'zh-hans': 'zh-cn',
		ja: 'ja',
		ko: 'ko',
	};
	return map[normalized] || 'en';
};

const setDocumentLang = value => {
	document.documentElement.setAttribute('lang', normalizeLang(value));
};

const appKey = ref(0);
const appReady = ref(false);
const layoutname = ref('auth');
const currentLayout = computed(
	() => components[layoutname.value] || components.auth
);
let autoThemeTimer = null;

watch(
	() => route.meta.layout,
	newLayout => {
		layoutname.value =
			newLayout !== undefined ? newLayout || 'auth' : 'auth';
	},
	{ immediate: true }
);

watch(
	() => store?.state?.settings?.config?.lang,
	value => {
		setDocumentLang(value);
	},
	{ immediate: true }
);

const bootstrapAppState = async () => {
	try {
		await Promise.all([loadAuth(store), loadConfig(store), loadDb(store)]);
		const auth = store.state.settings.auth;
		const hasValidSession = await ensureValidAuthSession(store);
		if (hasValidSession && auth.accessUID) {
			await applyAccountBrowserPreferences({
				store,
				orzuid: auth.accessUID,
				i18next,
			});
		} else {
			const preferredThemeMode = loadThemeModePreference(
				store.state.theme.themeMode
			);
			await store.dispatch(
				'changeThemeMode',
				resolveThemeMode(preferredThemeMode)
			);
		}
		await store.dispatch('initTheme');
		vm.$themeColor = store.state.settings.config.layout.themeColor;

		// Axios 先完成初始化；Feature Access menu 由受保護 route guard 在登入後載入。
		vm.$server = createServer(store, vm.$router);

		console.log(
			'app, bootstrap, auth-ready=',
			hasValidSession,
			', has-organization=',
			Boolean(auth.accessOUID)
		);
	} catch (error) {
		console.error('app, bootstrap, error=', error);
	} finally {
		// 初次 deep link 的 router guard 會等待這個 barrier，避免 axios 尚未建立就呼叫 API。
		markFeatureAccessBootstrapReady();
		appReady.value = true;
	}
};

const syncTimeBasedTheme = async () => {
	const preferredThemeMode = loadThemeModePreference(
		store.state.theme.themeMode
	);
	const effectiveThemeMode = resolveThemeMode(preferredThemeMode);
	if (effectiveThemeMode === store.state.theme.themeMode) return;

	await store.dispatch('changeThemeMode', effectiveThemeMode);
};

onBeforeMount(async () => {
	await bootstrapAppState();
});

onMounted(() => {
	console.log(
		'app, mounted, appname=',
		appname,
		', layout=name=',
		layoutname.value
	);

	appKey.value += 1;
	autoThemeTimer = window.setInterval(syncTimeBasedTheme, 60 * 1000);
});

onBeforeUnmount(() => {
	if (autoThemeTimer) window.clearInterval(autoThemeTimer);
});
</script>
